import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from '@mui/material'
import { saveAs } from 'file-saver'
import type { SuperDoc } from 'superdoc'
import 'superdoc/style.css'

import { Button, Icon, IconButton, Typography } from '~components/internal'

import './DocumentPreviewDialog.scss'

interface DocumentPreviewDialogProps {
  open: boolean
  onClose: () => void
  title: string
  file: Blob | null
  filename: string
  isLoading?: boolean
  error?: string | null
  onDownload?: () => void
  previewFooter?: React.ReactNode
  sidePanel?: React.ReactNode
  actions?: React.ReactNode
}

const DocumentPreviewDialog: React.FC<DocumentPreviewDialogProps> = ({
  open,
  onClose,
  title,
  file,
  filename,
  isLoading = false,
  error = null,
  onDownload,
  previewFooter,
  sidePanel,
  actions,
}) => {
  const lowercaseFilename = filename.toLowerCase()
  const isPdf = lowercaseFilename.endsWith('.pdf')
  const isDocx = lowercaseFilename.endsWith('.docx')

  const documentContainerRef = useRef<HTMLDivElement | null>(null)
  const [isRendering, setIsRendering] = useState(false)
  const [renderError, setRenderError] = useState<string | null>(null)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !file || !isPdf) return
    const objectUrl = URL.createObjectURL(new Blob([file], { type: 'application/pdf' }))
    setPdfUrl(objectUrl)
    return (): void => {
      URL.revokeObjectURL(objectUrl)
      setPdfUrl(null)
    }
  }, [open, file, isPdf])

  useEffect(() => {
    if (!open || !file || !isDocx) return
    let cancelled = false
    let superdocInstance: SuperDoc | null = null
    setIsRendering(true)
    setRenderError(null)

    void (async (): Promise<void> => {
      try {
        const [{ SuperDoc }, { superdocFonts }] = await Promise.all([
          import('superdoc'),
          import('@superdoc-dev/fonts'),
        ])
        if (cancelled || !documentContainerRef.current) return
        superdocInstance = new SuperDoc({
          selector: documentContainerRef.current,
          document: new File([file], filename),
          documentMode: 'viewing',
          role: 'viewer',
          ui: false,
          fonts: { ...superdocFonts },
          onReady: (): void => {
            if (!cancelled) setIsRendering(false)
          },
          onException: (): void => {
            if (cancelled) return
            setRenderError('This document could not be displayed.')
            setIsRendering(false)
          },
        })
      } catch {
        if (cancelled) return
        setRenderError('This document could not be displayed.')
        setIsRendering(false)
      }
    })()

    return (): void => {
      cancelled = true
      try {
        superdocInstance?.destroy()
      } catch {
        superdocInstance = null
      }
    }
  }, [open, file, filename, isDocx])

  const message = error ?? renderError ?? (!isPdf && !isDocx ? 'No preview available' : null)

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
      <DialogTitle className='document-preview-dialog__title'>
        {title}
        <Tooltip title='Close'>
          <IconButton variant='text' size='small' aria-label='Close' onClick={onClose}>
            <Icon name='close' fontSize='small' />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent className='document-preview-dialog__content'>
        <Box className='document-preview-dialog__document'>
          <Box className='document-preview-dialog__preview'>
            {(isLoading || isRendering) && !message && (
              <Box className='document-preview-dialog__overlay'>
                <CircularProgress color='inherit' />
              </Box>
            )}
            {message && (
              <Box className='document-preview-dialog__overlay'>
                <Typography variant='body2'>{message}</Typography>
              </Box>
            )}
            {isPdf && pdfUrl && !isLoading && (
              <iframe
                src={`${pdfUrl}#toolbar=0&navpanes=0`}
                title={filename}
                className='document-preview-dialog__pdf'
              />
            )}
            {isDocx && (
              <Box ref={documentContainerRef} className='document-preview-dialog__pages' />
            )}
          </Box>
          {previewFooter}
        </Box>
        {sidePanel && <Box className='document-preview-dialog__side-panel'>{sidePanel}</Box>}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onClose} className='document-preview-dialog__cancel'>
          Cancel
        </Button>
        {actions}
        <Button
          variant='contained'
          startIcon={<Icon name='download' />}
          disabled={isLoading || (!file && !onDownload)}
          onClick={() => (onDownload ? onDownload() : file && saveAs(file, filename))}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DocumentPreviewDialog
